/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Grid } from '../Grid';
import IconClose from '../icons/IconClose';

const Button = (props) => {
  const classes = classnames(
    'cmp-button',
    props.className,
    `cmp-button--${props.type}`,
    `cmp-button--${props.action}-action`,
    'font-body',
    {
      'cmp-button-wide': props.size === 'wide',
      'profile-background-color': props.action === 'profile',
      'profile-border-color': props.action === 'profile' && props.type === 'secondary',
    }
  );

  return (
    <button className={classes} disabled={props.isDisabled} onClick={props.onClick} style={props.style}>
      {props.children}
    </button>
  );
};
Button.propTypes = {
  children: PropTypes.any,
  onClick: PropTypes.func.isRequired,
  action: PropTypes.oneOf(['default', 'destructive', 'profile', 'backpack']),
  type: PropTypes.oneOf(['primary', 'secondary', 'modal']),
  size: PropTypes.oneOf(['wide']),
  effect: PropTypes.oneOf(['shadow', 'flat']),
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};
Button.defaultProps = {
  type: 'primary',
  action: 'default',
  isDisabled: false,
};

const PrimaryButton = (props) => {
  const classes = classnames(
    'cmp-button--primary',
    'font-body',
    props.className,
    {
      'cmp-button-wide': props.size === 'wide',
    }
  );

  return (
    <button className={classes} disabled={props.isDisabled} onClick={props.onClick} style={props.style}>
      {props.children}
    </button>
  );
};
PrimaryButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  onClick: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['wide']),
  isDisabled: PropTypes.bool,
  style: PropTypes.object,
};
PrimaryButton.defaultProps = {
  action: 'default',
  isDisabled: false,
};

export const ProfileThemedPrimaryButton = (props) => (
  <PrimaryButton type={null} {...props} className="profile-background-color-accent font-color-white" />
);

export const ProfileThemedBasePrimaryButton = (props) => (
  <PrimaryButton type={null} {...props} className="profile-background-color font-color-white" />
);

const SecondaryButton = (props) => {
  const classes = classnames(
    'cmp-button',
    'cmp-button--secondary',
    'cmp-button--default-action',
    props.className
  );

  // const styles = {
  //   backgroundColor: 'transparent',
  //   color
  // };

  return (
    <button className={classes} disabled={props.isDisabled} onClick={props.onClick} style={props.style}>
      {props.children}
    </button>
  );
};
SecondaryButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  onClick: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['wide']),
  isDisabled: PropTypes.bool,
  style: PropTypes.object,
};
SecondaryButton.defaultProps = {
  action: 'default',
  isDisabled: false,
};

export const GroupThemedSecondaryButton = (props) => (
  <SecondaryButton {...props} className="theme-group base border color" />
);

export const SmallSecondaryButton = (props) => (
  <SecondaryButton {...props} className={`${props.className} cmp-button--secondary-small`} />
)
SmallSecondaryButton.propTypes = SecondaryButton.propTypes;

const ButtonWithJustifiedContent = (props) => (
  <Button {...props}>
    <Grid justifyContent="center">
      {props.children}
    </Grid>
  </Button>
);
ButtonWithJustifiedContent.propTypes = {
  children: PropTypes.any.isRequired,
};

const CloseButton = (props) => {
  const mainStyle = classnames('button-close', {
    'align-right': props.align === 'right',
    'align-top': props.verticalAlign === 'top',
    'has-border': props.hasBorder,
    'has-dropshadow': props.hasDropshadow,
  });

  return (
    <button className={mainStyle} onClick={props.onClick}>
      <div className={classnames('button-close-background', props.className)} />
      <div className="button-close-icon">
        <IconClose.Small color="white" />
      </div>
    </button>
  );
};
CloseButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  align: PropTypes.oneOf(['right']),
  verticalAlign: PropTypes.oneOf(['top']),
  className: PropTypes.string,
  hasBorder: PropTypes.bool,
  hasDropshadow: PropTypes.bool,
};
CloseButton.defaultProps = {
  className: 'bkgd-color-dark-transparent-med-light',
};

const RoundedButton = (props) => (
  <Button {...props} className={classnames('cmp-button-rounded', props.className)}>
    {props.children}
  </Button>
);
RoundedButton.propTypes = {
  children: PropTypes.any.isRequired,
  className: PropTypes.string,
  type: PropTypes.string,
  action: PropTypes.string,
  onClick: PropTypes.func,
  style: PropTypes.any,
};

const TextButton = (props) => {
  const mainStyle = classnames('button-text', {
    'align-right': props.align === 'right',
  });

  return (
    <button className={mainStyle} onClick={props.onClick}>
      {props.children}
    </button>
  );
};
TextButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  children: PropTypes.any.isRequired,
  align: PropTypes.oneOf(['right']),
};

const DestructiveButton = (props) => {
  return (
    <Button {...props} className={['font-color-gsLightest', props.className].join(' ')} action="destructive" style={{ paddingLeft: 24, paddingRight: 24 }}>
      {props.children}
    </Button>
  );
};
DestructiveButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  onClick: PropTypes.func,
};

const ModalButton = (props) => {
  const classes = classnames(
    'cmp-button',
    'cmp-modalbutton',
    'font-title-2',
    props.className
  );

  return (
    <button className={classes} disabled={props.isDisabled} onClick={props.onClick} style={props.style}>
      {props.children}
    </button>
  );
}
ModalButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  onClick: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  style: PropTypes.object,
};
ModalButton.defaultProps = {
  isDisabled: false,
};

const ModalButtonsPrimary = (props) => (
  <ModalButton {...props} className={classnames(props.className, 'cmp-button--primary font-color-gsLightest')} />
);
ModalButtonsPrimary.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.any,
};

const ModalButtonsSecondary = (props) => (
  <ModalButton {...props} className={classnames(props.className, 'cmp-button--secondary')} />
);
ModalButtonsSecondary.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.any,
};
const ModalButtons = {
  Primary: ModalButtonsPrimary,
  Secondary: ModalButtonsSecondary,
};


export {
  Button,
  SecondaryButton,
  DestructiveButton,
  ButtonWithJustifiedContent,
  CloseButton,
  RoundedButton,
  TextButton,
  ModalButtons,
};
